import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";

/** kb-data-api KB config path via the backoffice proxy. */
const KB_CONFIG_PATH = "/proxy/kb-data/api/v1/config";
/** settings-api integration-instances path via the backoffice proxy. */
const INTEGRATION_INSTANCES_PATH =
  "/proxy/settings/api/v1/integration-instances";
/** integrations service credentials list path via the backoffice proxy. */
const CREDENTIALS_PATH = "/proxy/integrations/api/v1/credentials";

/** Category the integrations service assigns to knowledge-base integrations. */
const KNOWLEDGE_BASE_CATEGORY = "knowledge-base";
/**
 * Confluence provider identifiers. Confluence tags documents with the
 * ``space_key`` the SDA filters on, so public scope keys are Confluence-only.
 */
const CONFLUENCE_PROVIDERS = new Set(["confluence", "confluence-api-token"]);

/**
 * Providers whose sources can be overridden per-source. Mirrors kb-core
 * ``PERMISSION_AWARE_KB_TYPES``: the backend rejects a ``source_access_modes``
 * key that resolves to any other kb_type.
 */
const PERMISSION_AWARE_PROVIDERS = new Set([
  "confluence",
  "confluence-api-token",
  "microsoft-sharepoint",
  "simpplr",
  "workvivo",
  "freshservice",
  "google-drive",
  "guru",
]);

/** Per-source override of the tenant-wide access mode. */
export type SourceAccessMode = "public" | "private";

/** Mirrors kb-data-api KBConfigResponse. */
export interface KBConfig {
  access_mode: "public" | "private";
  public_scope_keys: string[];
  show_sources: boolean;
  /**
   * Per-source overrides of ``access_mode``, keyed by the integration
   * instance id. Sources absent from the map inherit the tenant-wide
   * ``access_mode``.
   */
  source_access_modes: Record<string, SourceAccessMode>;
}

/** One selectable scope key, from the integrations service. */
export interface ScopeKeyOption {
  value: string;
  label: string;
  provider: string;
}

/** Maximum public scope keys the backend accepts (kb_core MAX_PUBLIC_SCOPE_KEYS). */
export const MAX_PUBLIC_SCOPE_KEYS = 10;

/** Read a tenant's KB access configuration (defaults applied server-side). */
export function useKBConfig(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["kb-config", "config", tenantId],
    queryFn: async (): Promise<KBConfig> => {
      // Type the map as optional on the wire so the default below is meaningful:
      // an older response may omit it, but the returned KBConfig never does.
      const { data } = await backofficeClient.get<
        Omit<KBConfig, "source_access_modes"> & {
          source_access_modes?: Record<string, SourceAccessMode>;
        }
      >(KB_CONFIG_PATH, { params: { tenant_id: tenantId } });
      return { ...data, source_access_modes: data.source_access_modes ?? {} };
    },
    enabled: Boolean(tenantId),
  });
}

/** A knowledge-base source whose access mode can be overridden per-source. */
export interface KBSource {
  /** Integration instance id — the ``source_access_modes`` key. */
  id: string;
  /** Display name for the instance. */
  name: string;
  /** Provider slug (e.g. ``confluence``, ``microsoft-sharepoint``). */
  provider: string;
}

/**
 * List the tenant's permission-aware knowledge-base sources (Confluence,
 * SharePoint, Simpplr, Workvivo, Freshservice, Google Drive and Guru
 * instances). Keyed by the integration instance ``id``, which is the same
 * value ``source_access_modes`` uses.
 */
export function useKBSources(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["kb-config", "sources", tenantId],
    queryFn: async (): Promise<KBSource[]> => {
      const { data } = await backofficeClient.get<IntegrationInstancesResponse>(
        INTEGRATION_INSTANCES_PATH,
        { params: { tenant_id: tenantId } },
      );
      return data.integration_instances
        .filter(
          (instance) =>
            PERMISSION_AWARE_PROVIDERS.has(instance.integration_id) &&
            instance.enabled !== false,
        )
        .map((instance) => ({
          id: instance.id,
          name: instance.name || instance.integration_id,
          provider: instance.integration_id,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: Boolean(tenantId),
  });
}

/** Subset of settings-api IntegrationInstance we rely on. */
interface IntegrationInstance {
  id: string;
  integration_id: string;
  name?: string;
  enabled?: boolean;
  configuration_data?: Record<string, unknown> | null;
}

interface IntegrationInstancesResponse {
  integration_instances: IntegrationInstance[];
}

/**
 * Normalize a credential's ``selected_spaces`` value into a list of keys. The
 * value is stored inconsistently — sometimes a real array, sometimes a
 * JSON-encoded string — so accept both and drop anything unparseable.
 */
function parseSelectedSpaces(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw.filter((item): item is string => typeof item === "string");
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (item): item is string => typeof item === "string",
        );
      }
    } catch {
      // Fallback: Python-repr string like ['a', 'b'] — only swap boundary
      // quotes so an embedded double-quote doesn't break parsing.
      try {
        const jsonCompatible = raw
          .replace(/^\['/, '["')
          .replace(/'\]$/, '"]')
          .replace(/',\s*'/g, '", "');
        const parsed: unknown = JSON.parse(jsonCompatible);
        if (Array.isArray(parsed)) {
          return parsed.filter(
            (item): item is string => typeof item === "string",
          );
        }
      } catch {
        // Unparseable — nothing selectable.
      }
    }
  }
  return [];
}

/**
 * List the tenant's available public scope keys, aggregated from the selected
 * spaces of its Confluence knowledge-base integrations.
 *
 * The scope keys mirror the ``selected_spaces`` shown in the settings
 * integrations UI. Only Confluence tags KB documents with the matching
 * ``space_key`` the SDA filters on, so keys are sourced from Confluence
 * instances only. The instance list omits configuration, so each Confluence
 * instance is fetched individually to read its cleartext ``selected_spaces``.
 */
export function useKBScopeKeys(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["kb-config", "scope-keys", tenantId],
    queryFn: async (): Promise<ScopeKeyOption[]> => {
      const { data } = await backofficeClient.get<IntegrationInstancesResponse>(
        INTEGRATION_INSTANCES_PATH,
        { params: { tenant_id: tenantId } },
      );
      const confluenceInstances = data.integration_instances.filter(
        (instance) =>
          CONFLUENCE_PROVIDERS.has(instance.integration_id) &&
          instance.enabled !== false,
      );

      // Fetch per-instance config independently: one instance failing to load
      // must not blank the whole picker, so we keep the ones that succeed.
      const settled = await Promise.allSettled(
        confluenceInstances.map(async (instance) => {
          const { data: full } =
            await backofficeClient.get<IntegrationInstance>(
              `${INTEGRATION_INSTANCES_PATH}/${instance.id}`,
              { params: { tenant_id: tenantId } },
            );
          return full;
        }),
      );
      const instanceConfigs = settled
        .filter(
          (result): result is PromiseFulfilledResult<IntegrationInstance> =>
            result.status === "fulfilled",
        )
        .map((result) => result.value);

      // First provider seen for a key wins, so results are stable.
      const seen = new Map<string, ScopeKeyOption>();
      for (const instance of instanceConfigs) {
        const spaces = parseSelectedSpaces(
          instance.configuration_data?.selected_spaces,
        );
        for (const key of spaces) {
          if (key && !seen.has(key)) {
            seen.set(key, {
              value: key,
              label: key,
              provider: instance.integration_id,
            });
          }
        }
      }

      return [...seen.values()].sort((a, b) => a.value.localeCompare(b.value));
    },
    enabled: Boolean(tenantId),
  });
}

interface CredentialSummary {
  id: string;
  provider: string;
  category: string;
  enabled: boolean;
}

interface CredentialsListResponse {
  credentials: CredentialSummary[];
  total: number;
}

export interface KBIntegrationsInfo {
  /** The tenant has at least one enabled knowledge-base integration. */
  hasKnowledgeBase: boolean;
  /** The tenant has at least one enabled Confluence knowledge-base integration. */
  hasConfluence: boolean;
  /** The tenant has at least one enabled permission-aware KB integration. */
  hasPermissionAware: boolean;
}

/**
 * Inspect a tenant's knowledge-base integrations to decide whether
 * permission-aware (private) access is supported. The tenant-wide access mode
 * applies to every ``PERMISSION_AWARE_PROVIDERS`` integration.
 */
export function useKBIntegrations(tenantId: string | undefined) {
  return useQuery({
    queryKey: ["kb-config", "kb-integrations", tenantId],
    queryFn: async (): Promise<KBIntegrationsInfo> => {
      const { data } = await backofficeClient.get<CredentialsListResponse>(
        CREDENTIALS_PATH,
        { params: { tenant_id: tenantId } },
      );
      const kbCreds = data.credentials.filter(
        (c) => c.category === KNOWLEDGE_BASE_CATEGORY && c.enabled,
      );
      return {
        hasKnowledgeBase: kbCreds.length > 0,
        hasConfluence: kbCreds.some((c) => CONFLUENCE_PROVIDERS.has(c.provider)),
        hasPermissionAware: kbCreds.some((c) =>
          PERMISSION_AWARE_PROVIDERS.has(c.provider),
        ),
      };
    },
    enabled: Boolean(tenantId),
  });
}

/** Persist the whole KB config (wholesale replace). */
export function useUpdateKBConfig(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: KBConfig) => {
      const { data } = await backofficeClient.put<KBConfig>(
        KB_CONFIG_PATH,
        body,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["kb-config", "config", tenantId],
      });
    },
  });
}
